import type {
  ClosingPackageItemState,
  ClosingPackagePhase,
  ClosingPackageTaskSummary,
} from '../types/closingPackage';
import type { Route } from '../types';
import { TASK_TYPE_LABELS } from '../constants/controlTasks';
import { TASK_REGISTRY } from '../constants/taskRegistry';

import { resolveDocumentStatusMeta } from './documentStatusDisplay';

/** Подписи состояния позиции комплекта (DOC-013 — через единый status helper). */
export const CLOSING_PACKAGE_ITEM_STATE_META: Record<
  ClosingPackageItemState,
  { label: string; color: string }
> = {
  MISSING: resolveDocumentStatusMeta('MISSING'),
  UPLOADED: resolveDocumentStatusMeta('UPLOADED'),
  SENT: resolveDocumentStatusMeta('SENT'),
  RECEIVED: resolveDocumentStatusMeta('RECEIVED'),
  CONFIRMED: resolveDocumentStatusMeta('CONFIRMED'),
};

/** Read-only подписи фазы комплекта (из Backend Projection). */
export const CLOSING_PACKAGE_PHASE_META: Record<
  ClosingPackagePhase,
  { label: string; color: string }
> = {
  NOT_AVAILABLE: { label: 'Недоступен', color: 'default' },
  COLLECTING: { label: 'Формирование комплекта', color: 'orange' },
  READY_TO_SEND: { label: 'Комплект готов к отправке', color: 'blue' },
  DISPATCHED: { label: 'Отправлен клиенту на согласование', color: 'processing' },
  RECEIVED_BY_CLIENT: { label: 'Получен клиентом', color: 'green' },
};

/** Ожидания, отображаемые в блоке клиентского комплекта (IMP-001B). */
export const CLOSING_PACKAGE_DISPLAY_BLOCKER_TYPES = new Set([
  'UPLOAD_UPD',
  'UPD_UPLOAD',
  'CREATE_CLIENT_INVOICE',
  'TN_ORIGINAL_MISSING',
  'UPLOAD_TN_ORIGINAL',
  'ORIGINAL_TRANSPORT_DOC_MISSING',
]);

/** Исключённые из блока комплекта: Finance перевозчика и workflow следующих этапов. */
export const CLOSING_PACKAGE_EXCLUDED_BLOCKER_TYPES = new Set([
  'CARRIER_INVOICE_MISSING',
  'CARRIER_INVOICE_CONFIRM',
  'INVOICE_EXPECTED',
  'CONFIRM_UPD_SENT',
  'CONFIRM_INVOICE_SENT',
  'PAYMENT_EXPECTED',
  'EXPENSE_CONFIRMATION',
]);

const TN_MISSING_BLOCKER_TYPES = new Set([
  'TN_ORIGINAL_MISSING',
  'UPLOAD_TN_ORIGINAL',
  'ORIGINAL_TRANSPORT_DOC_MISSING',
]);

/**
 * Подписи ожиданий (blockers) для блока комплекта.
 * Приоритет над TASK_REGISTRY — формулировки для read-only контекста «ожидает выполнения».
 */
export const CLOSING_PACKAGE_BLOCKER_LABELS: Record<string, string> = {
  UPLOAD_UPD: 'Необходимо загрузить УПД',
  UPD_UPLOAD: 'Не загружен УПД',
  CREATE_CLIENT_INVOICE: 'Ожидается счёт клиента',
  TN_ORIGINAL_MISSING: 'Отсутствуют оригиналы ТН/ТТН клиента',
  UPLOAD_TN_ORIGINAL: 'Отсутствуют оригиналы ТН/ТТН клиента',
  ORIGINAL_TRANSPORT_DOC_MISSING: 'Отсутствуют оригиналы ТН/ТТН клиента',
};

export const CLOSING_PACKAGE_MISSING_DOCUMENT_TEXT = 'Документ отсутствует';

export const CLOSING_PACKAGE_TN_ITEM_KEY = 'CLIENT_TN_TTN_PACKAGE';

export type ClosingPackageBlockerLine =
  | { kind: 'tag'; key: string; label: string }
  | { kind: 'tnRoutes'; key: string; title: string; routes: string[] };

export function closingPackageItemStateMeta(state: ClosingPackageItemState) {
  return CLOSING_PACKAGE_ITEM_STATE_META[state] ?? CLOSING_PACKAGE_ITEM_STATE_META.MISSING;
}

export function closingPackagePhaseMeta(phase: ClosingPackagePhase) {
  return CLOSING_PACKAGE_PHASE_META[phase] ?? CLOSING_PACKAGE_PHASE_META.NOT_AVAILABLE;
}

export function isClosingPackageTnMissingBlocker(taskType: string): boolean {
  return TN_MISSING_BLOCKER_TYPES.has(String(taskType || '').trim().toUpperCase());
}

export function isClosingPackageDisplayBlocker(task: ClosingPackageTaskSummary): boolean {
  const normalized = String(task.taskType || '').trim().toUpperCase();
  if (!normalized || task.status === 'COMPLETED') return false;
  if (CLOSING_PACKAGE_EXCLUDED_BLOCKER_TYPES.has(normalized)) return false;
  return CLOSING_PACKAGE_DISPLAY_BLOCKER_TYPES.has(normalized);
}

export function filterClosingPackageDisplayBlockers(
  blockers: ClosingPackageTaskSummary[]
): ClosingPackageTaskSummary[] {
  const seen = new Set<number>();
  return blockers.filter((task) => {
    if (!isClosingPackageDisplayBlocker(task)) return false;
    if (seen.has(task.id)) return false;
    seen.add(task.id);
    return true;
  });
}

export function formatClosingPackageRouteLabel(
  routeId: number | null | undefined,
  routes: Route[]
): string | null {
  if (routeId == null) return null;
  const route = routes.find((item) => item.id === routeId);
  const from = route?.loadingCity || route?.loadingAddress;
  const to = route?.unloadingCity || route?.unloadingAddress;
  if (from && to) return `Маршрут №${routeId}: ${from} → ${to}`;
  return `Маршрут №${routeId}`;
}

export function formatClosingPackageRouteNumber(
  routeId: number | null | undefined
): string | null {
  if (routeId == null) return null;
  return `Маршрут №${routeId}`;
}

/** Русская подпись blocker taskType без показа технического enum. */
export function closingPackageBlockerLabel(taskType: string): string {
  const normalized = String(taskType || '').trim().toUpperCase();
  if (!normalized) return 'Требуется выполнение задачи';

  const closingLabel = CLOSING_PACKAGE_BLOCKER_LABELS[normalized];
  if (closingLabel) return closingLabel;

  const registryLabel = TASK_REGISTRY[normalized]?.label;
  if (registryLabel) return registryLabel;

  const controlLabel = TASK_TYPE_LABELS[normalized];
  if (controlLabel) return controlLabel;

  return 'Требуется выполнение задачи';
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function buildClosingPackageBlockerDisplay(
  blockers: ClosingPackageTaskSummary[],
  routes: Route[]
): ClosingPackageBlockerLine[] {
  const allowed = filterClosingPackageDisplayBlockers(blockers);
  const tnTasks = allowed.filter((task) => isClosingPackageTnMissingBlocker(task.taskType));
  const otherTasks = allowed.filter((task) => !isClosingPackageTnMissingBlocker(task.taskType));

  const lines: ClosingPackageBlockerLine[] = [];

  if (tnTasks.length > 0) {
    const routeLabels = uniqueStrings(
      tnTasks
        .map((task) => formatClosingPackageRouteLabel(task.routeId, routes))
        .filter((value): value is string => Boolean(value))
    );

    if (routeLabels.length === 0) {
      lines.push({
        kind: 'tag',
        key: 'tn-missing',
        label: 'Отсутствуют оригиналы ТН/ТТН клиента',
      });
    } else if (routeLabels.length === 1) {
      lines.push({
        kind: 'tag',
        key: 'tn-missing',
        label: `Отсутствуют оригиналы ТН/ТТН (${routeLabels[0]})`,
      });
    } else {
      lines.push({
        kind: 'tnRoutes',
        key: 'tn-missing',
        title: 'Ожидаются оригиналы ТН/ТТН:',
        routes: routeLabels,
      });
    }
  }

  for (const task of otherTasks) {
    lines.push({
      kind: 'tag',
      key: String(task.id),
      label: closingPackageBlockerLabel(task.taskType),
    });
  }

  return lines;
}

export function resolveClosingPackageItemRouteSubtitle(params: {
  itemKey: string;
  documents: Array<{ route?: { id: number; label: string | null } | null }>;
}): string | null {
  if (params.itemKey !== CLOSING_PACKAGE_TN_ITEM_KEY) return null;

  const routeIds = Array.from(
    new Set(
      params.documents
        .map((document) => document.route?.id)
        .filter((routeId): routeId is number => routeId != null)
    )
  );

  if (routeIds.length !== 1) return null;
  return formatClosingPackageRouteNumber(routeIds[0]);
}
